import React, { useMemo, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import '../../../../../../../Stylesheets/Dashboard/Tabs/Labor/LogPaymentFromDashboardModal.css';
import { logWorkerPayment, refreshLaborProject } from '../../../../../../../Redux/Projects/Labor/laborSlice';
import { showFeedback } from '../../../../../../../Redux/UI/uiFeedbackSlice';
import { storage } from '../../../../../../../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

const LogPaymentFromDashboardModal = ({ projectId, onClose }) => {
  const dispatch = useDispatch();
  const { workers = [], outstanding = {} } = useSelector((state) => state.labor || {});
  const outstandingList = outstanding?.list || [];

  const [selectedWorkerId, setSelectedWorkerId] = useState('');
  const [date, setDate] = useState(new Date());
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [receipt, setReceipt] = useState(null);
  const [includeInExpense, setIncludeInExpense] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // safer worker filter
  const projectWorkers = useMemo(() => {
    return workers.filter((w) => String(w.projectId) === String(projectId));
  }, [workers, projectId]);

  const selectedWorker = useMemo(() => {
    if (!selectedWorkerId) return null;
    const base = projectWorkers.find((w) => String(w._id) === String(selectedWorkerId));
    const out = outstandingList.find((x) => String(x.workerId) === String(selectedWorkerId));
    if (!base) return null;
    return { ...base, outstanding: Number(out?.outstanding || 0) };
  }, [selectedWorkerId, projectWorkers, outstandingList]);

  // autofill amount from outstanding once worker selected
  useEffect(() => {
    if (!selectedWorkerId || !selectedWorker) {
      setAmount('');
      return;
    }
    const out = Number(selectedWorker.outstanding || 0);
    setAmount(out > 0 ? String(out) : '');
  }, [selectedWorkerId, selectedWorker]);

  const amountError = useMemo(() => {
    if (!selectedWorkerId) return 'Select a worker';
    const n = Number(amount);
    if (!amount) return 'Amount is required';
    if (!Number.isFinite(n) || n <= 0) return 'Amount must be greater than 0';
    if (n > 100000000) return 'Amount looks too large';
    return '';
  }, [selectedWorkerId, amount]);

  const onReceiptPick = (e) => {
    const f = e.target.files?.[0] || null;
    setReceipt(f);
    // allow choosing same file again later
    e.target.value = '';
  };

  const handleSubmit = async () => {
    if (!projectId) {
      dispatch(showFeedback({ type: 'error', title: 'No project', message: 'Project id missing.' }));
      return;
    }

    if (amountError) {
      dispatch(showFeedback({ type: 'error', title: 'Fix required fields', message: amountError }));
      return;
    }

    if (!selectedWorker) {
      dispatch(showFeedback({ type: 'error', title: 'Worker missing', message: 'Selected worker not found in this project.' }));
      return;
    }

    if (!date || !(date instanceof Date) || Number.isNaN(date.getTime())) {
      dispatch(showFeedback({ type: 'error', title: 'Invalid date', message: 'Please select a valid date.' }));
      return;
    }

    try {
      setIsSubmitting(true);

      let receiptUrl = '';
      let receiptName = '';

      if (receipt) {
        // sanitize file ext
        const ext = (receipt.name.split('.').pop() || 'bin').toLowerCase().replace(/[^a-z0-9]/g, '');
        const safeExt = ext || 'bin';

        const storagePath = `receipts/${projectId}/${selectedWorkerId}/${Date.now()}.${safeExt}`;
        const storageRef = ref(storage, storagePath);

        await uploadBytes(storageRef, receipt, { contentType: receipt.type || 'application/octet-stream' });
        receiptUrl = await getDownloadURL(storageRef);
        receiptName = receipt.name;
      }

      const payload = {
        projectId,
        amount: Number(amount),
        date: date.toISOString(),
        note: note?.trim() || '',
        mode: 'cash',
        receiptName,
        receiptUrl,
        createExpense: includeInExpense,
      };

      const action = await dispatch(logWorkerPayment({ workerId: selectedWorkerId, data: payload }));

      if (logWorkerPayment.rejected?.match?.(action)) {
        const msg = action.payload?.message || action.error?.message || 'Payment logging failed.';
        dispatch(showFeedback({ type: 'error', title: 'Failed', message: msg }));
        return;
      }

      // keep the labor UI in sync (metrics, outstanding, activity, etc.)
      await dispatch(refreshLaborProject(projectId));

      const updatedOutstanding = action.payload?.updatedOutstanding;
      const expense = action.payload?.expense;

      dispatch(
        showFeedback({
          type: 'success',
          title: 'Payment logged',
          message: expense
            ? `₹${Number(amount).toLocaleString()} logged. Also added to Expenses.`
            : `₹${Number(amount).toLocaleString()} logged successfully.`,
        })
      );

      onClose();
    } catch (err) {
      console.error('Payment logging failed', err);
      dispatch(showFeedback({ type: 'error', title: 'Error', message: err?.message || 'Something went wrong.' }));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="log-dashboard-modal-overlay" onMouseDown={onClose}>
      <div className="log-dashboard-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="log-dashboard-header">
          <h3>Log Payment</h3>
          <button onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="log-dashboard-group">
          <label>Worker name</label>
          <select
            className="log-dashboard-input"
            value={selectedWorkerId}
            onChange={(e) => setSelectedWorkerId(e.target.value)}
            disabled={isSubmitting}
          >
            <option value="">Select Worker</option>
            {projectWorkers.map((w) => (
              <option key={w._id} value={w._id}>
                {w.firstName} {w.lastName}
              </option>
            ))}
          </select>

          {selectedWorker && (
            <div className="log-dashboard-outstanding">
              {Number(selectedWorker.outstanding || 0) > 0 ? (
                <>Outstanding Amount: ₹ <span>{selectedWorker.outstanding}</span>/-</>
              ) : (
                <span className="outstanding-grey">No Outstanding Amount</span>
              )}
            </div>
          )}
        </div>

        <div className="log-dashboard-group">
          <label>Date</label>
          <DatePicker
            selected={date}
            onChange={(d) => setDate(d || new Date())}
            className="log-dashboard-input"
            dateFormat="dd MMMM yyyy"
            disabled={isSubmitting}
          />
        </div>

        <div className="log-dashboard-group">
          <label>Amount</label>
          <input
            type="number"
            placeholder="₹ Amount"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="log-dashboard-input"
            disabled={isSubmitting}
          />
          {amountError && <div className="input-error">{amountError}</div>}
        </div>

        <div className="log-dashboard-group">
          <label>Notes (Optional)</label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="log-dashboard-input"
            rows="3"
            disabled={isSubmitting}
          />
        </div>

        <div className="log-dashboard-group">
          <label>Upload Receipt</label>
          <div className="log-dashboard-upload-wrapper">
            <label htmlFor="receipt-upload" className="log-dashboard-upload-btn" style={isSubmitting ? { opacity: 0.7, pointerEvents: 'none' } : undefined}>
              ⬆ Upload file
            </label>
            <input
              id="receipt-upload"
              type="file"
              onChange={onReceiptPick}
              style={{ display: 'none' }}
              disabled={isSubmitting}
              accept="image/*,application/pdf"
            />
            {receipt && <span className="log-dashboard-upload-name">{receipt.name}</span>}
          </div>
        </div>

        <div className="log-dashboard-group">
          <label className="log-dashboard-checkbox-label">
            <input
              type="checkbox"
              checked={includeInExpense}
              onChange={(e) => setIncludeInExpense(e.target.checked)}
              disabled={isSubmitting}
            />
            <span>Also add this payment as an Expense entry</span>
          </label>
        </div>

        <div className="log-dashboard-footer">
          <button
            type="button"
            className="btn black"
            onClick={handleSubmit}
            disabled={!!amountError || isSubmitting}
          >
            {isSubmitting ? 'Logging…' : 'Log Payment'}
          </button>

          <button
            type="button"
            className="btn cancel"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>

        </div>
      </div>
    </div>
  );
};

export default LogPaymentFromDashboardModal;
